/**
 * Cyphex Teams — WebSocket wire protocol.
 *
 * This file is the SINGLE SOURCE OF TRUTH for every frame that crosses the
 * gateway. apps/gateway must accept exactly these client frames and emit
 * exactly these server frames. apps/web must handle every server frame.
 *
 * Full prose spec: docs/REALTIME-PROTOCOL.md
 *
 * DESIGN RULES
 *  1. The WebSocket is a DELIVERY channel, not an RPC channel. All
 *     state-changing operations go over REST. The only exceptions are
 *     ephemeral signals that must never touch the database: typing, presence
 *     heartbeat, meeting reactions, raise hand, captions.
 *  2. Every persisted event carries a monotonic `seq` scoped to its topic.
 *     Clients detect gaps by seq, never by timestamp.
 *  3. Frames are JSON. Binary frames are reserved and currently unused.
 */

import type {
  CallId,
  ChannelId,
  ConversationId,
  IsoDateTime,
  MeetingId,
  MessageId,
  Seq,
  TeamId,
  TenantId,
  Uuid,
  UserId,
} from './common';
import type { Message, MessageDraft, Reaction, MessagePreview } from './chat';
import type { PresenceSummary } from './presence';
import type { MeetingParticipant, MeetingQuestion } from './meetings';
import type { Call, IncomingCallPayload } from './calls';
import type { Notification } from './notifications';
import type { UserRef } from './identity';

/** Bumped only on a breaking change. The server rejects unknown majors. */
export const PROTOCOL_VERSION = 1;

/* ===========================================================================
 * TOPICS — the unit of subscription and of sequencing.
 * ========================================================================= */

/**
 * A topic string. Grammar: `<kind>:<uuid>` or `user:<uuid>`.
 *   conversation:<conversationId>   messages, typing, reactions, receipts
 *   channel:<channelId>             channel metadata, tabs, pins
 *   team:<teamId>                   team metadata, membership
 *   meeting:<meetingId>             participants, hands, polls, Q&A, captions
 *   call:<callId>                   call state transitions
 *   user:<userId>                   the caller's PRIVATE topic: notifications,
 *                                   incoming calls, presence of subscriptions,
 *                                   unread counters, settings changes
 *   presence:<userId>               presence of ONE other user
 *   tenant:<tenantId>               admin broadcasts, branding changes
 */
export type Topic = string;

export const topics = {
  conversation: (id: ConversationId): Topic => `conversation:${id}`,
  channel: (id: ChannelId): Topic => `channel:${id}`,
  team: (id: TeamId): Topic => `team:${id}`,
  meeting: (id: MeetingId): Topic => `meeting:${id}`,
  call: (id: CallId): Topic => `call:${id}`,
  user: (id: UserId): Topic => `user:${id}`,
  presence: (id: UserId): Topic => `presence:${id}`,
  tenant: (id: TenantId): Topic => `tenant:${id}`,
  /**
   * A webinar's audience-facing topic. Deliberately NOT the meeting topic
   * underneath it: that one carries the full participant roster, which for a
   * 2 000-seat event is both enormous and none of an attendee's business.
   */
  webinar: (id: Uuid): Topic => `webinar:${id}`,
} as const;

/* ===========================================================================
 * FRAME ENVELOPES
 * ========================================================================= */

export interface ClientFrameBase {
  /** Protocol version. Must equal PROTOCOL_VERSION. */
  v: number;
  /** Client-generated frame id; echoed in the matching ack/error. */
  id: string;
}

export interface ServerFrameBase {
  v: number;
  /** Server frame id. Also used for de-duplication after a reconnect. */
  id: string;
  /** Server wall clock. FOR DISPLAY ONLY — never use it for ordering. */
  ts: IsoDateTime;
}

/* ---------------------------------------------------------------------------
 * CLIENT -> SERVER
 * ------------------------------------------------------------------------- */

/**
 * First frame after the socket opens. The server replies `hello.ok` or closes
 * with 4401. No other frame is accepted before the handshake completes.
 */
export interface ClientHello extends ClientFrameBase {
  t: 'hello';
  /** Same access token as the REST API. */
  token: string;
  deviceId?: Uuid;
  clientVersion: string;
  /**
   * Resume state. When present the server replays everything the client
   * missed instead of forcing a cold reload.
   */
  resume?: {
    sessionId: string;
    /** Highest seq the client holds, per topic. */
    cursors: Record<Topic, Seq>;
  };
  /** Topics to subscribe to immediately, saving a round trip. */
  subscribe?: Topic[];
}

/** Liveness + presence heartbeat. Expected every 25s. */
export interface ClientPing extends ClientFrameBase {
  t: 'ping';
  /** ms since the user last interacted; drives auto-away. */
  idleMs?: number;
}

export interface ClientSubscribe extends ClientFrameBase {
  t: 'sub';
  topics: Topic[];
  /** Deliver missed events on these topics from this seq onwards. */
  cursors?: Record<Topic, Seq>;
}

export interface ClientUnsubscribe extends ClientFrameBase {
  t: 'unsub';
  topics: Topic[];
}

/**
 * Explicit gap repair. Sent when the client sees a seq jump, or after
 * regaining focus. The server responds with `catchup.begin`, a run of
 * `event` frames, then `catchup.end`.
 */
export interface ClientResync extends ClientFrameBase {
  t: 'resync';
  topic: Topic;
  fromSeq: Seq;
  /** Server clamps to 500. If more is missing it replies `resync.too_far`. */
  limit?: number;
}

/** Ephemeral. Never persisted. Server rate-limits to 1 per 3s per conversation. */
export interface ClientTyping extends ClientFrameBase {
  t: 'typing';
  conversationId: ConversationId;
  state: 'start' | 'stop';
  /** Typing inside a thread shows the indicator only in that thread. */
  threadRootId?: MessageId;
}

/** Client-driven presence hint (focus/blur, screen lock). */
export interface ClientPresence extends ClientFrameBase {
  t: 'presence';
  activity: 'active' | 'idle' | 'background';
}

/**
 * Viewport declaration for a meeting. THIS IS A DENSITY-CRITICAL FRAME:
 * the server uses it to tell the SFU which tracks this client actually needs,
 * so a 200-person meeting forwards ~12 streams instead of 200.
 */
export interface ClientMeetingViewport extends ClientFrameBase {
  t: 'meeting.viewport';
  meetingId: MeetingId;
  /** Participant ids currently rendered, with the CSS pixel size of each tile. */
  visible: Array<{ participantId: Uuid; width: number; height: number }>;
  /** Audio-only mode: the client wants zero video tracks. */
  audioOnly?: boolean;
}

/** Ephemeral meeting signals. */
export interface ClientMeetingSignal extends ClientFrameBase {
  t: 'meeting.signal';
  meetingId: MeetingId;
  signal:
    | { kind: 'hand'; raised: boolean }
    | { kind: 'reaction'; emoji: string }
    | { kind: 'caption'; text: string; language: string; isFinal: boolean };
}

/** Acknowledge receipt of server frames up to this id (backpressure control). */
export interface ClientAck extends ClientFrameBase {
  t: 'ack';
  /** Highest server frame id processed. */
  upTo: string;
}

export type ClientFrame =
  | ClientHello
  | ClientPing
  | ClientSubscribe
  | ClientUnsubscribe
  | ClientResync
  | ClientTyping
  | ClientPresence
  | ClientMeetingViewport
  | ClientMeetingSignal
  | ClientAck;

/* ---------------------------------------------------------------------------
 * SERVER -> CLIENT
 * ------------------------------------------------------------------------- */

export interface ServerHelloOk extends ServerFrameBase {
  t: 'hello.ok';
  /** Pass this back in `resume` on reconnect. Valid for 5 minutes offline. */
  sessionId: string;
  userId: UserId;
  tenantId: TenantId;
  /** Send `ping` at least this often or the server closes with 4408. */
  heartbeatIntervalMs: number;
  /** Topics the server auto-subscribed (always includes `user:<self>`). */
  subscribed: Topic[];
  /** True when `resume` was accepted; false means the client must cold-load. */
  resumed: boolean;
  serverTime: IsoDateTime;
}

export interface ServerPong extends ServerFrameBase {
  t: 'pong';
  /** Echo of the ping frame id. */
  ackId: string;
}

export interface ServerSubscribed extends ServerFrameBase {
  t: 'sub.ok';
  ackId: string;
  /** Current high-water seq per topic, so the client can detect its own gaps. */
  topics: Record<Topic, Seq>;
  /** Topics refused, with a reason. */
  denied?: Array<{ topic: Topic; reason: 'forbidden' | 'not_found' | 'limit' }>;
}

/** Wraps every domain event. */
export interface ServerEvent<T extends RealtimeEvent = RealtimeEvent> extends ServerFrameBase {
  t: 'event';
  topic: Topic;
  /** Monotonic within `topic`. Gaps mean the client missed something. */
  seq: Seq;
  event: T;
}

export interface ServerCatchupBegin extends ServerFrameBase {
  t: 'catchup.begin';
  topic: Topic;
  fromSeq: Seq;
  toSeq: Seq;
  count: number;
}

export interface ServerCatchupEnd extends ServerFrameBase {
  t: 'catchup.end';
  topic: Topic;
  toSeq: Seq;
  /** True when more remains; the client should issue another `resync`. */
  truncated: boolean;
}

/**
 * The client fell too far behind to replay. It must discard local state for
 * this topic and refetch over REST. Emitted instead of a 500-frame flood.
 */
export interface ServerResyncTooFar extends ServerFrameBase {
  t: 'resync.too_far';
  topic: Topic;
  /** Oldest seq still replayable. */
  oldestSeq: Seq;
  currentSeq: Seq;
}

/**
 * BACKPRESSURE. The server's per-connection send buffer is above the high
 * watermark. The client must stop expanding subscriptions and may reduce its
 * meeting viewport. If the client does not drain, the server drops LOW
 * priority events (typing, presence, reactions) and finally closes with 4429.
 */
export interface ServerBackpressure extends ServerFrameBase {
  t: 'backpressure';
  level: 'warn' | 'shedding' | 'critical';
  /** Event classes currently being dropped. */
  shedding: Array<'typing' | 'presence' | 'reaction' | 'caption'>;
  bufferedBytes: number;
}

export interface ServerError extends ServerFrameBase {
  t: 'error';
  /** Frame id this error responds to, when applicable. */
  ackId?: string;
  code:
    | 'bad_frame'
    | 'unsupported_version'
    | 'unauthenticated'
    | 'token_expired'
    | 'forbidden'
    | 'not_found'
    | 'rate_limited'
    | 'subscription_limit'
    | 'internal_error';
  message: string;
  retryAfter?: number;
}

/** The server is shutting down or rebalancing. Reconnect after the delay. */
export interface ServerReconnect extends ServerFrameBase {
  t: 'reconnect';
  afterMs: number;
  reason: 'shutdown' | 'rebalance' | 'session_expired' | 'token_expired';
  /** Resume is possible when true; otherwise cold-load. */
  resumable: boolean;
}

export type ServerFrame =
  | ServerHelloOk
  | ServerPong
  | ServerSubscribed
  | ServerEvent
  | ServerCatchupBegin
  | ServerCatchupEnd
  | ServerResyncTooFar
  | ServerBackpressure
  | ServerError
  | ServerReconnect;

/* ===========================================================================
 * EVENT CATALOGUE
 * Every event is `{ type, ...payload }`. The `type` values here are the
 * authoritative list — the REST layer, the outbox `event_type` column, and
 * webhook `event_types` all use these exact strings.
 * ========================================================================= */

/* ---- Messaging ---------------------------------------------------------- */

export interface MessageNewEvent {
  type: 'message.new';
  conversationId: ConversationId;
  message: Message;
  /** Echo of the sender's clientMsgId so the optimistic row can be replaced. */
  clientMsgId: Uuid | null;
  /** Recomputed unread for the receiving user. Absent on the sender's own. */
  unread?: { count: number; mentionCount: number };
}

export interface MessageUpdatedEvent {
  type: 'message.updated';
  conversationId: ConversationId;
  messageId: MessageId;
  seq: Seq;
  /** Full replacement of the mutable fields. */
  message: Message;
  editedAt: IsoDateTime;
}

export interface MessageDeletedEvent {
  type: 'message.deleted';
  conversationId: ConversationId;
  messageId: MessageId;
  seq: Seq;
  deletedBy: UserId;
  /** `soft` leaves a tombstone; `hard` means remove it from the UI entirely. */
  mode: 'soft' | 'hard';
}

export interface MessagePinnedEvent {
  type: 'message.pinned' | 'message.unpinned';
  conversationId: ConversationId;
  messageId: MessageId;
  actorId: UserId;
}

export interface ReactionEvent {
  type: 'reaction.added' | 'reaction.removed';
  conversationId: ConversationId;
  messageId: MessageId;
  userId: UserId;
  emoji: string;
  /** Full recomputed pill row, so the client never has to merge counts. */
  reactions: Reaction[];
}

export interface TypingEvent {
  type: 'typing.start' | 'typing.stop';
  conversationId: ConversationId;
  threadRootId: MessageId | null;
  user: UserRef;
  /** Auto-expire at this time even if no `typing.stop` arrives. */
  expiresAt: IsoDateTime;
}

export interface ReadReceiptEvent {
  type: 'read.receipt';
  conversationId: ConversationId;
  userId: UserId;
  /** Everything up to and including this seq is read. */
  seq: Seq;
  at: IsoDateTime;
}

export interface ConversationUpdatedEvent {
  type: 'conversation.updated';
  conversationId: ConversationId;
  /** Only the changed fields. */
  patch: Partial<{
    title: string | null;
    avatarColor: string;
    archivedAt: IsoDateTime | null;
    ttlSeconds: number | null;
    lastMessage: MessagePreview | null;
  }>;
}

export interface ConversationMemberEvent {
  type: 'conversation.member.added' | 'conversation.member.removed';
  conversationId: ConversationId;
  actorId: UserId | null;
  users: UserRef[];
}

/**
 * Cross-device draft sync. Delivered on the caller's PRIVATE `user:<id>` topic —
 * a draft is nobody else's business. Volatile: losing one costs a stale
 * composer on a second device until the next conversation open, never data.
 */
export interface ConversationDraftEvent {
  type: 'conversation.draft.updated';
  conversationId: ConversationId;
  /** `null` when the draft was cleared (sent or emptied). */
  draft: MessageDraft | null;
  updatedAt: IsoDateTime | null;
}

/** Personal Saved queue changed. Also on the private `user:<id>` topic. */
export interface MessageSavedEvent {
  type: 'message.saved' | 'message.unsaved';
  conversationId: ConversationId;
  messageId: MessageId;
  savedAt: IsoDateTime | null;
}

/** A reminder was armed or dropped. Private topic; the FIRING is a notification. */
export interface MessageReminderEvent {
  type: 'message.reminder.set' | 'message.reminder.cleared';
  conversationId: ConversationId;
  messageId: MessageId;
  remindAt: IsoDateTime | null;
}

export interface UnreadChangedEvent {
  type: 'unread.changed';
  /** Delivered on the private `user:<id>` topic. */
  conversations: Array<{
    conversationId: ConversationId;
    unreadCount: number;
    unreadMentionCount: number;
    lastSeq: Seq;
  }>;
  totals: { unread: number; mentions: number };
}

export interface PollUpdatedEvent {
  type: 'poll.updated' | 'poll.closed';
  conversationId: ConversationId | null;
  meetingId: MeetingId | null;
  messageId: MessageId | null;
  poll: import('./chat').Poll;
}

/* ---- Teams & channels --------------------------------------------------- */

export interface ChannelEvent {
  type: 'channel.created' | 'channel.updated' | 'channel.archived' | 'channel.deleted';
  teamId: TeamId;
  channelId: ChannelId;
  channel?: import('./teams').Channel;
  actorId: UserId | null;
}

export interface ChannelMemberEvent {
  type: 'channel.member.added' | 'channel.member.removed' | 'channel.member.role_changed';
  channelId: ChannelId;
  /**
   * The channel's conversation. On a removal the gateway closes the removed
   * people's live `conversation:<id>` subscription as well as the channel one.
   */
  conversationId?: ConversationId;
  users: UserRef[];
  role?: string;
  actorId: UserId | null;
}

export interface TeamEvent {
  type: 'team.created' | 'team.updated' | 'team.archived' | 'team.deleted';
  teamId: TeamId;
  team?: import('./teams').Team;
  actorId: UserId | null;
}

export interface TeamMemberEvent {
  type: 'team.member.added' | 'team.member.removed' | 'team.member.role_changed';
  teamId: TeamId;
  users: UserRef[];
  role?: string;
  actorId: UserId | null;
}

/* ---- Presence ----------------------------------------------------------- */

export interface PresenceUpdateEvent {
  type: 'presence.update';
  userId: UserId;
  presence: PresenceSummary;
}

/** Batched presence for large rosters — sent instead of N single updates. */
export interface PresenceBulkEvent {
  type: 'presence.bulk';
  updates: Array<{ userId: UserId; presence: PresenceSummary }>;
}

/* ---- Calls -------------------------------------------------------------- */

export interface CallIncomingEvent {
  type: 'call.incoming';
  call: IncomingCallPayload;
}

export interface CallStateEvent {
  type:
    | 'call.ringing'
    | 'call.accepted'
    | 'call.declined'
    | 'call.connected'
    | 'call.hold'
    | 'call.resumed'
    | 'call.transferring'
    | 'call.transferred'
    | 'call.merged'
    | 'call.ended'
    | 'call.missed';
  callId: CallId;
  call: Call;
  actorId: UserId | null;
  reason?: string;
}

/**
 * THE CALL BECAME A MEETING - and everybody who was ON it has to end up there.
 *
 * IT IS DELIBERATELY NOT A `CallStateEvent`, and that is the whole fix. As one
 * it carried the meeting id in `reason` - a free-text field no client reads -
 * so the only party who ever reached the meeting was the one whose own HTTP
 * response told them its id. Everybody else got a call that ended for no stated
 * reason and, up to 30 seconds later, a generic "Call ended".
 *
 * WHY THE JOIN CODE IS ON THE WIRE. The escalated meeting is not one the others
 * were invited to: `GET /meetings/:id` 404s for them, because they are not its
 * organiser, not an invitee and not yet a participant. `codeGrantsEntry`
 * (services/meetingAccess.ts) admits any workspace MEMBER holding the code and
 * its comment names that the "forward the invite" flow, so the code IS the
 * entitlement and `POST /meetings/join-by-code` is the door. Seeding
 * `meeting_invitees` instead would also work and would be wrong: that writes a
 * meeting INVITATION - a notification row, an Activity entry, an RSVP to answer
 * - for a room the person is being walked into this second.
 *
 * `movedUserIds` is read BEFORE the call's participants are settled to `left`,
 * so it names who was actually in the conversation. `call.participants` cannot
 * answer that by the time the event is built: on it, everyone reads `left`.
 */
export interface CallEscalatedEvent {
  type: 'call.escalated';
  callId: CallId;
  call: Call;
  /** Who pressed "Move to meeting". They are already on their way there. */
  actorId: UserId | null;
  meetingId: MeetingId;
  /**
   * Bearer credential for `POST /meetings/join-by-code` — and ABSENT on the copy
   * of this event that goes to everybody else.
   *
   * `call.escalated` is fanned out TWICE, deliberately, and the difference
   * between the two copies is this key. `audienceOf` is every user who ever had
   * a row on the call — a person who DECLINED it an hour ago included — and they
   * still have to learn the call is over. But the code is not news, it is a
   * credential: `codeGrantsEntry` (apps/api services/meetingAccess.ts) admits any
   * workspace member holding it, so putting it on the broad copy handed the
   * decliner a working key to a meeting they were never moved to. The gateway
   * cannot save us there — `deliverToTopic` encodes ONE frame and sends the same
   * Buffer to every subscriber, and the `call:<id>` topic is itself unfiltered
   * (apps/gateway db/queries.ts `Q_ACL_CALLS` matches any participant row in any
   * state) — so the narrowing has to happen where the event is BUILT.
   *
   * Optional, therefore, and a client that finds it missing must do nothing:
   * this copy is an announcement, not an invitation.
   */
  joinCode?: string;
  /** Everyone who was LIVE on the call, the actor included. */
  movedUserIds: UserId[];
}

export interface CallParticipantEvent {
  type: 'call.participant.joined' | 'call.participant.left' | 'call.participant.updated';
  callId: CallId;
  participant: import('./calls').CallParticipant;
}

/** Cancels the incoming-call popup on this user's OTHER devices. */
export interface CallCancelledEvent {
  type: 'call.cancelled';
  callId: CallId;
  /** Which device answered/declined, so the others know why. */
  handledByDeviceId: Uuid | null;
  outcome: 'answered_elsewhere' | 'declined_elsewhere' | 'cancelled_by_caller' | 'timeout';
}

/* ---- Meetings ----------------------------------------------------------- */

export interface MeetingLifecycleEvent {
  type: 'meeting.started' | 'meeting.ended' | 'meeting.updated' | 'meeting.cancelled';
  meetingId: MeetingId;
  meeting: import('./meetings').Meeting;
  actorId: UserId | null;
}

export interface MeetingParticipantEvent {
  type:
    | 'meeting.participant.joined'
    | 'meeting.participant.left'
    | 'meeting.participant.updated'
    | 'meeting.participant.removed';
  meetingId: MeetingId;
  participant: MeetingParticipant;
}

export interface MeetingLobbyEvent {
  type: 'meeting.lobby.waiting' | 'meeting.lobby.admitted' | 'meeting.lobby.denied';
  meetingId: MeetingId;
  entries: import('./meetings').LobbyEntry[];
  actorId: UserId | null;
}

export interface MeetingHandEvent {
  type: 'meeting.hand.raised' | 'meeting.hand.lowered';
  meetingId: MeetingId;
  participantId: Uuid;
  /** Ordered queue so the UI can show "3rd in line". */
  queue: Uuid[];
}

export interface MeetingReactionEvent {
  type: 'meeting.reaction';
  meetingId: MeetingId;
  participantId: Uuid;
  emoji: string;
}

export interface MeetingRecordingEvent {
  type:
    | 'meeting.recording.started'
    | 'meeting.recording.paused'
    | 'meeting.recording.resumed'
    | 'meeting.recording.stopped'
    | 'meeting.recording.ready';
  meetingId: MeetingId;
  recording: import('./meetings').MeetingRecording;
  actorId: UserId | null;
}

export interface MeetingBreakoutEvent {
  type: 'meeting.breakout.opened' | 'meeting.breakout.closed' | 'meeting.breakout.assigned';
  meetingId: MeetingId;
  rooms: import('./meetings').BreakoutRoom[];
  /** Set when THIS client must move rooms. */
  moveTo?: { roomId: Uuid; sfuRoom: string; token: string };
}

export interface MeetingQaEvent {
  type: 'meeting.qa.asked' | 'meeting.qa.answered' | 'meeting.qa.updated';
  meetingId: MeetingId;
  question: MeetingQuestion;
}

/**
 * A poll opened, voted on, or closed INSIDE a meeting.
 *
 * Deliberately NOT `poll.updated`: that event carries the chat-shaped `Poll`
 * (with its `createdBy` UserRef and its message anchor) and is folded into the
 * message cache by the chat reducer. A meeting poll is rendered by the meeting
 * panel from a flatter object, and giving one event two payload shapes is how a
 * reducer starts guessing which one it just received.
 */
export interface MeetingPollEvent {
  type: 'meeting.poll.updated' | 'meeting.poll.closed';
  meetingId: MeetingId;
  poll: import('./meetings').MeetingPoll;
}

/** The shared notes textarea was saved. Carries the text, not a diff. */
export interface MeetingNotesEvent {
  type: 'meeting.notes.updated';
  meetingId: MeetingId;
  notes: import('./meetings').MeetingNotes;
  actorId: UserId | null;
}

/**
 * A message was posted in the meeting's chat panel.
 *
 * It rides the `meeting:` topic as well as the `message.new` on the underlying
 * conversation, because those are two different audiences: everyone in the room
 * has the meeting topic open and almost nobody has the conversation open.
 */
export interface MeetingChatEvent {
  type: 'meeting.chat.new';
  meetingId: MeetingId;
  message: import('./meetings').MeetingChatMessage;
}

export interface MeetingCaptionEvent {
  type: 'meeting.caption';
  meetingId: MeetingId;
  participantId: Uuid;
  text: string;
  language: string;
  isFinal: boolean;
}

/**
 * Server-directed subscription change. Sent when the SFU or the admission
 * controller decides this client should reduce or change what it receives —
 * e.g. the box is under load, or the active speaker changed while paginated.
 */
export interface MeetingSubscriptionEvent {
  type: 'meeting.subscription.update';
  meetingId: MeetingId;
  /** Participant ids the client SHOULD have video subscriptions for. */
  subscribeVideo: Uuid[];
  unsubscribeVideo: Uuid[];
  /** Per-participant quality ceiling the client must request. */
  quality: Record<Uuid, 'high' | 'medium' | 'low' | 'off'>;
  reason: 'viewport' | 'active_speaker' | 'server_load' | 'client_cpu' | 'bandwidth';
}

export interface MeetingActiveSpeakerEvent {
  type: 'meeting.active_speaker';
  meetingId: MeetingId;
  /** Ordered loudest-first. Drives the purple ring on the tile. */
  participantIds: Uuid[];
}

/* ---- Whiteboard ---------------------------------------------------------- */

/**
 * A board was created, renamed, frozen, deleted, or shared onto a meeting
 * stage. Delivered on `channel:<channelId>` for channel boards and on
 * `meeting:<meetingId>` for the stage transitions.
 *
 * WHAT THIS EVENT DELIBERATELY DOES NOT CARRY: drawing. Strokes travel over the
 * yjs socket to apps/collab, which is the whole point of a CRDT — routing a
 * hundred pointer samples a second through the durable outbox would flood the
 * gateway and still arrive too late to feel live.
 *
 * `whiteboard.updated` is what a client acts on when a host freezes a board:
 * the payload says `isLocked`, and a client whose own edit rights may have
 * changed re-requests its session token rather than trusting a flag in a frame.
 * A capability that a client can grant itself by ignoring an event is not a
 * capability.
 */
export interface WhiteboardEvent {
  type:
    | 'whiteboard.created'
    | 'whiteboard.updated'
    | 'whiteboard.deleted'
    | 'whiteboard.opened'
    | 'whiteboard.closed';
  board: import('./whiteboard').WhiteboardSummary;
  /** Set for `opened`/`closed` — which meeting stage this concerns. */
  meetingId: MeetingId | null;
  actorId: UserId | null;
}

/* ---- Persistent voice rooms --------------------------------------------- */

/**
 * Live occupancy of ONE voice channel, carrying the FULL occupant list rather
 * than a delta.
 *
 * Full-state was chosen for the same reason `reaction.*` re-sends the whole
 * pill row: the sidebar renders this for every voice channel of every team, and
 * a client that merges joins against leaves will eventually show a ghost
 * occupant who left while the tab was backgrounded. A voice room holds tens of
 * people, not thousands, so the payload is small enough that correctness wins.
 *
 * DURABLE on purpose. It is emitted through the outbox inside the transaction
 * that wrote the session row, which is what gives it a `topic_seq` and makes it
 * replayable on reconnect — a volatile publish carries no seq and the gateway
 * drops it for exactly that reason (apps/gateway core/deliver.ts).
 *
 * Delivered on `team:<teamId>`: every active member of the team may see the
 * room, and that is precisely the audience the sidebar needs.
 */
export interface VoiceOccupancyEvent {
  type: 'voice.occupancy';
  teamId: TeamId;
  channelId: ChannelId;
  occupants: import('./voice').VoiceOccupant[];
  /** Who moved and how — drives the join/leave chime. Null on a cold sync. */
  change: import('./voice').VoiceOccupancyChange | null;
}

/* ---- Webinars + town halls ---------------------------------------------- */

/**
 * The webinar's own lifecycle, delivered on `webinar:<webinarId>`.
 *
 * A SEPARATE TOPIC from the meeting beneath it, and that is the point: the
 * meeting topic carries the roster of everyone connected, which for a 2 000-seat
 * event is both enormous and none of an attendee's business. The webinar topic
 * carries only what an attendee is entitled to see.
 *
 * `webinar.golive` is the transition every presenter client acts on: it must
 * leave the practice room and reconnect to the broadcast room. The event
 * carries no token — the client re-calls join, so a stale event can never
 * resurrect a grant that has since been revoked.
 */
export interface WebinarLifecycleEvent {
  type:
    | 'webinar.published'
    | 'webinar.practice'
    | 'webinar.golive'
    | 'webinar.ended'
    | 'webinar.cancelled'
    | 'webinar.updated';
  webinarId: Uuid;
  status: import('./webinars').WebinarStatus;
  /** Live viewer count at the moment of the transition. */
  viewerCount: number;
}

/** Moderated Q&A. `asked` reaches moderators; `published` reaches everyone. */
export interface WebinarQaEvent {
  type: 'webinar.qa.asked' | 'webinar.qa.published' | 'webinar.qa.updated';
  webinarId: Uuid;
  question: import('./webinars').WebinarQuestion;
}

export interface WebinarPollEvent {
  type: 'webinar.poll.opened' | 'webinar.poll.closed' | 'webinar.poll.results';
  webinarId: Uuid;
  poll: import('./webinars').WebinarPoll;
}

/**
 * Attendance gauge. VOLATILE: it is a number on a badge, it is re-derived on
 * every join and leave, and replaying a viewer count from ten minutes ago would
 * be worse than showing nothing.
 */
export interface WebinarAttendanceEvent {
  type: 'webinar.attendance';
  webinarId: Uuid;
  viewerCount: number;
}

/** An RTMP destination changed state. Organiser console only. */
export interface WebinarStreamEvent {
  type: 'webinar.stream.updated';
  webinarId: Uuid;
  stream: import('./webinars').WebinarStream;
}

/* ---- Notifications, files, tasks, admin --------------------------------- */

/**
 * Why a delivery channel stayed quiet. Mirrors
 * `notification_deliveries.suppress_reason`.
 */
export type NotificationSuppressReason =
  | 'quiet_hours'
  | 'dnd'
  | 'muted'
  | 'already_read'
  | 'rule'
  | 'level'
  | 'channel_off'
  | 'no_subscription'
  | 'transient';

/**
 * The SERVER'S interruption verdict, decided once against the recipient's own
 * settings, quiet-hours timezone and live presence.
 *
 * It rides on the event because the alternative — every client deriving it
 * locally — is not a policy at all: a second signed-in session pops the banner
 * the first one correctly suppressed, and a client whose settings have not
 * hydrated yet suppresses nothing. Clients render this; they do not re-decide
 * it.
 */
export interface NotificationDelivery {
  banner: boolean;
  sound: boolean;
  desktop: boolean;
  reason: NotificationSuppressReason | null;
}

export interface NotificationPushEvent {
  type: 'notification.push';
  notification: Notification;
  counts: { unseen: number; unread: number; mentions: number };
  /** Absent on events emitted before 055; treat a missing verdict as "allow". */
  delivery?: NotificationDelivery;
}

/**
 * A NOTIFICATION THAT IS NO LONGER TRUE, WITHDRAWN BY THE SERVER.
 *
 * Not the same as `notification.read`: the user did nothing. The row was
 * retired because the thing it announced ended — the canonical case being a
 * `call_incoming` alert for a call that has now been answered, declined or
 * missed. Before this existed the only server-side signal at those moments was
 * `call.cancelled`, which dismisses a POPUP and cannot touch the Activity feed,
 * so the ring stayed in the bell for ever.
 *
 * DURABLE, and that is not a default. `notification.push` is durable, so a
 * client resuming after an outage replays the ring; if the withdrawal were
 * volatile it would be dropped and the row would come back from the dead.
 */
export interface NotificationDismissedEvent {
  type: 'notification.dismissed';
  /** The rows that were removed or rewritten. May be empty on a no-op. */
  notificationIds: Uuid[];
  /** The group key those rows carried, so a client can also drop by key. */
  groupKey: string | null;
}

export interface NotificationReadEvent {
  type: 'notification.read' | 'notification.seen';
  /** Empty array = "all". */
  notificationIds: Uuid[];
  counts: { unseen: number; unread: number; mentions: number };
}

export interface FileEvent {
  type: 'file.uploaded' | 'file.updated' | 'file.deleted' | 'file.ready';
  file: import('./files').CyphexFile;
  scopeKind: string;
  scopeId: Uuid;
  actorId: UserId | null;
}

export interface TaskEvent {
  type: 'task.created' | 'task.updated' | 'task.completed' | 'task.deleted';
  task: import('./admin').Task;
  actorId: UserId | null;
}

export interface ApprovalEvent {
  type: 'approval.requested' | 'approval.responded' | 'approval.completed';
  approval: import('./admin').Approval;
  actorId: UserId | null;
}

/**
 * Custom emoji changed. Delivered on `tenant:<tenantId>` so every open picker
 * updates without polling — a new emoji nobody can see until their next reload
 * is a feature that reads as broken.
 */
export interface CustomEmojiEvent {
  type: 'emoji.created' | 'emoji.deleted';
  emoji: import('./admin').CustomEmoji;
  actorId: UserId | null;
}

/** A moderator removed someone else's message. Delivered on `channel:<id>`. */
export interface ModerationEvent {
  type: 'moderation.action';
  channelId: ChannelId | null;
  action: import('./admin').ModerationAction;
}

/**
 * A workspace member arrived, or was switched off. Delivered on
 * `tenant:<tenantId>`.
 *
 * It exists because the PUBLIC catalogue in platform.ts promises
 * `user.joined` / `user.deactivated` to webhook subscribers, and the relay can
 * only publish what the outbox contains. Without a durable internal event those
 * two entries were subscribable, acknowledged — and then never fired, which
 * costs an integrator a day of debugging their own receiver before they think
 * to suspect us.
 *
 * `source` names the path that provisioned or removed the account: in a
 * workspace running SCIM, SSO and manual invitations at once, "where did this
 * person come from" is the first question anyone asks of a joiner event.
 */
export interface UserLifecycleEvent {
  type: 'user.joined' | 'user.deactivated';
  user: {
    id: UserId;
    username: string;
    displayName: string;
    email: string | null;
    /** `member` | `guest` | `bot`. */
    kind: string;
  };
  /** `register` | `invite` | `admin` | `scim` | `sso` | `external`. */
  source: string;
  actorId: UserId | null;
}

export interface TenantEvent {
  type: 'tenant.updated' | 'tenant.branding_changed' | 'tenant.announcement';
  tenantId: TenantId;
  message?: string;
  patch?: Record<string, unknown>;
}

/** Forces the client to re-fetch its token/permissions immediately. */
export interface SessionEvent {
  type: 'session.revoked' | 'session.permissions_changed';
  reason: string;
}

export type RealtimeEvent =
  | MessageNewEvent
  | MessageUpdatedEvent
  | MessageDeletedEvent
  | MessagePinnedEvent
  | ReactionEvent
  | TypingEvent
  | ReadReceiptEvent
  | ConversationUpdatedEvent
  | ConversationMemberEvent
  | ConversationDraftEvent
  | MessageSavedEvent
  | MessageReminderEvent
  | UnreadChangedEvent
  | PollUpdatedEvent
  | ChannelEvent
  | ChannelMemberEvent
  | TeamEvent
  | TeamMemberEvent
  | PresenceUpdateEvent
  | PresenceBulkEvent
  | CallIncomingEvent
  | CallStateEvent
  | CallEscalatedEvent
  | CallParticipantEvent
  | CallCancelledEvent
  | MeetingLifecycleEvent
  | MeetingParticipantEvent
  | MeetingLobbyEvent
  | MeetingHandEvent
  | MeetingReactionEvent
  | MeetingRecordingEvent
  | MeetingBreakoutEvent
  | MeetingQaEvent
  | MeetingPollEvent
  | MeetingNotesEvent
  | MeetingChatEvent
  | MeetingCaptionEvent
  | MeetingSubscriptionEvent
  | MeetingActiveSpeakerEvent
  | WhiteboardEvent
  | VoiceOccupancyEvent
  | WebinarLifecycleEvent
  | WebinarQaEvent
  | WebinarPollEvent
  | WebinarAttendanceEvent
  | WebinarStreamEvent
  | NotificationPushEvent
  | NotificationDismissedEvent
  | NotificationReadEvent
  | FileEvent
  | TaskEvent
  | ApprovalEvent
  | CustomEmojiEvent
  | ModerationEvent
  | UserLifecycleEvent
  | TenantEvent
  | SessionEvent;

export type RealtimeEventType = RealtimeEvent['type'];

/**
 * Delivery class. Determines what gets dropped first under backpressure and
 * whether the event is persisted for catch-up.
 *   durable  — persisted, replayable via `resync`, never dropped
 *   volatile — best-effort, dropped under load, never replayed
 */
export const EVENT_DELIVERY: Record<RealtimeEventType, 'durable' | 'volatile'> = {
  'message.new': 'durable',
  'message.updated': 'durable',
  'message.deleted': 'durable',
  'message.pinned': 'durable',
  'message.unpinned': 'durable',
  'reaction.added': 'durable',
  'reaction.removed': 'durable',
  'typing.start': 'volatile',
  'typing.stop': 'volatile',
  'read.receipt': 'volatile',
  'conversation.updated': 'durable',
  'conversation.member.added': 'durable',
  'conversation.member.removed': 'durable',
  // All four ride the caller's private topic and are re-derivable from REST on
  // the next open, so they are best-effort by design.
  'conversation.draft.updated': 'volatile',
  'message.saved': 'volatile',
  'message.unsaved': 'volatile',
  'message.reminder.set': 'volatile',
  'message.reminder.cleared': 'volatile',
  'unread.changed': 'volatile',
  'poll.updated': 'durable',
  'poll.closed': 'durable',
  'channel.created': 'durable',
  'channel.updated': 'durable',
  'channel.archived': 'durable',
  'channel.deleted': 'durable',
  'channel.member.added': 'durable',
  'channel.member.removed': 'durable',
  'channel.member.role_changed': 'durable',
  'team.created': 'durable',
  'team.updated': 'durable',
  'team.archived': 'durable',
  'team.deleted': 'durable',
  'team.member.added': 'durable',
  'team.member.removed': 'durable',
  'team.member.role_changed': 'durable',
  'presence.update': 'volatile',
  'presence.bulk': 'volatile',
  'call.incoming': 'durable',
  'call.ringing': 'durable',
  'call.accepted': 'durable',
  'call.declined': 'durable',
  'call.connected': 'durable',
  'call.hold': 'durable',
  'call.resumed': 'durable',
  'call.transferring': 'durable',
  'call.transferred': 'durable',
  'call.merged': 'durable',
  'call.escalated': 'durable',
  'call.ended': 'durable',
  'call.missed': 'durable',
  'call.cancelled': 'durable',
  'call.participant.joined': 'durable',
  'call.participant.left': 'durable',
  'call.participant.updated': 'volatile',
  'meeting.started': 'durable',
  'meeting.ended': 'durable',
  'meeting.updated': 'durable',
  'meeting.cancelled': 'durable',
  'meeting.participant.joined': 'durable',
  'meeting.participant.left': 'durable',
  'meeting.participant.updated': 'volatile',
  'meeting.participant.removed': 'durable',
  'meeting.lobby.waiting': 'durable',
  'meeting.lobby.admitted': 'durable',
  'meeting.lobby.denied': 'durable',
  'meeting.hand.raised': 'volatile',
  'meeting.hand.lowered': 'volatile',
  'meeting.reaction': 'volatile',
  'meeting.recording.started': 'durable',
  'meeting.recording.paused': 'durable',
  'meeting.recording.resumed': 'durable',
  'meeting.recording.stopped': 'durable',
  'meeting.recording.ready': 'durable',
  'meeting.breakout.opened': 'durable',
  'meeting.breakout.closed': 'durable',
  'meeting.breakout.assigned': 'durable',
  'meeting.qa.asked': 'durable',
  'meeting.qa.answered': 'durable',
  'meeting.qa.updated': 'durable',
  // Durable, all five: a poll opened, a vote counted, a note saved and a chat
  // line are each a fact the room must agree on. Dropping one under load would
  // leave two people looking at different tallies of the same poll.
  'meeting.poll.updated': 'durable',
  'meeting.poll.closed': 'durable',
  'meeting.notes.updated': 'durable',
  'meeting.chat.new': 'durable',
  'meeting.caption': 'volatile',
  'meeting.subscription.update': 'volatile',
  'meeting.active_speaker': 'volatile',
  // Durable: a board that was opened while a client was reconnecting must still
  // appear on their stage, and the payload is one small object, not a stroke.
  'whiteboard.created': 'durable',
  'whiteboard.updated': 'durable',
  'whiteboard.deleted': 'durable',
  'whiteboard.opened': 'durable',
  'whiteboard.closed': 'durable',
  'voice.occupancy': 'durable',
  // Every webinar lifecycle transition is durable: a presenter whose tab was
  // backgrounded through `golive` must still learn it has to move rooms, and
  // outbox replay on reconnect is exactly what guarantees that.
  'webinar.published': 'durable',
  'webinar.practice': 'durable',
  'webinar.golive': 'durable',
  'webinar.ended': 'durable',
  'webinar.cancelled': 'durable',
  'webinar.updated': 'durable',
  'webinar.qa.asked': 'durable',
  'webinar.qa.published': 'durable',
  'webinar.qa.updated': 'durable',
  'webinar.poll.opened': 'durable',
  'webinar.poll.closed': 'durable',
  'webinar.poll.results': 'durable',
  // A gauge. Re-derived on every join and leave; replaying a ten-minute-old
  // viewer count would be worse than showing none.
  'webinar.attendance': 'volatile',
  'webinar.stream.updated': 'durable',
  'notification.push': 'durable',
  // See NotificationDismissedEvent: it must replay behind the push it undoes.
  'notification.dismissed': 'durable',
  'notification.read': 'volatile',
  'notification.seen': 'volatile',
  'file.uploaded': 'durable',
  'file.updated': 'durable',
  'file.deleted': 'durable',
  'file.ready': 'durable',
  'task.created': 'durable',
  'task.updated': 'durable',
  'task.completed': 'durable',
  'task.deleted': 'durable',
  'approval.requested': 'durable',
  'approval.responded': 'durable',
  'approval.completed': 'durable',
  'emoji.created': 'durable',
  'emoji.deleted': 'durable',
  'moderation.action': 'durable',
  // Durable: the webhook relay tails the OUTBOX, so a joiner event published
  // volatile would never reach a subscription.
  'user.joined': 'durable',
  'user.deactivated': 'durable',
  'tenant.updated': 'durable',
  'tenant.branding_changed': 'durable',
  'tenant.announcement': 'durable',
  'session.revoked': 'durable',
  'session.permissions_changed': 'durable',
};

/** WebSocket close codes used by the gateway. */
export const WS_CLOSE = {
  NORMAL: 1000,
  /** Handshake failed or token invalid. */
  UNAUTHENTICATED: 4401,
  /** Token expired mid-session; reconnect with a fresh one. */
  TOKEN_EXPIRED: 4403,
  /** Heartbeat missed. */
  TIMEOUT: 4408,
  /** Client ignored backpressure. */
  BACKPRESSURE: 4429,
  /** Protocol version unsupported. */
  BAD_VERSION: 4400,
  /** Server shutting down; resume is possible. */
  GOING_AWAY: 4503,
} as const;
